import * as yup from 'yup';
import {parseRegion} from '~/lib/config';
import {StoredJsonEntry} from './storage';
import {clearViewerSessionForOrganization} from './viewer-session-storage';

const credentialSchema = yup
  .object({
    token: yup.string().required(),
    expiresAt: yup
      .string()
      .required()
      .test('unexpired', (value) => {
        const expiresAt = Date.parse(value);
        return Number.isFinite(expiresAt) && expiresAt > Date.now();
      }),
    // Absent on legacy page-scoped entries, which were always verified.
    anonymous: yup.boolean(),
  })
  .required();

type StoredRefreshToken = yup.InferType<typeof credentialSchema>;

interface RefreshCredential extends StoredRefreshToken {
  storageKey: string;
}

const pointerSchema = yup.string().required();

const ORGANIZATION_KEY_PREFIX = 'refresh-token:organization:';

const organizationStorageKey = (organizationIdentifier: string) =>
  `${ORGANIZATION_KEY_PREFIX}${organizationIdentifier}`;
const pageOrganizationStorageKey = (pageIdentifier: string) =>
  `refresh-token:page-organization:${pageIdentifier}`;
const lastOrganizationStorageKey = (identifier: string) =>
  `refresh-token:last-organization:${parseRegion(identifier)}`;

// Tokens were historically stored per page. Read this key as a migration
// fallback until a successful response tells us which organization owns it.
const legacyStorageKey = (pageIdentifier: string) =>
  `refresh-token:${pageIdentifier}`;

const credentialEntry = (key: string) =>
  new StoredJsonEntry(() => localStorage, key, credentialSchema);
const pointerEntry = (key: string) =>
  new StoredJsonEntry(() => localStorage, key, pointerSchema);

function readCredential(storageKey: string): RefreshCredential | null {
  const stored = credentialEntry(storageKey).get();
  return stored ? {...stored, storageKey} : null;
}

function readOrganizationPointer(pointerKey: string): RefreshCredential | null {
  const pointer = pointerEntry(pointerKey);
  const organizationIdentifier = pointer.get();
  if (!organizationIdentifier) {
    return null;
  }

  const credential = readCredential(
    organizationStorageKey(organizationIdentifier),
  );
  if (!credential) {
    pointer.clear();
  }
  return credential;
}

function writeOrganizationPointers(
  pageIdentifier: string,
  organizationIdentifier: string,
): void {
  pointerEntry(pageOrganizationStorageKey(pageIdentifier)).set(
    organizationIdentifier,
  );
  pointerEntry(lastOrganizationStorageKey(organizationIdentifier)).set(
    organizationIdentifier,
  );
}

function getRefreshCredential(
  pageIdentifier: string,
): RefreshCredential | null {
  const mapped = readOrganizationPointer(
    pageOrganizationStorageKey(pageIdentifier),
  );
  if (mapped) {
    return mapped;
  }

  const legacy = readCredential(legacyStorageKey(pageIdentifier));
  if (legacy) {
    return legacy;
  }

  // A brand-new page URL does not expose its organization identifier. Try the
  // most recently used credential in the same deployment region; the backend
  // safely ignores cross-organization credentials. Once a request succeeds,
  // the page-to-organization mapping makes future selection exact.
  return readOrganizationPointer(lastOrganizationStorageKey(pageIdentifier));
}

export function getRefreshToken(pageIdentifier: string): string | null {
  return getRefreshCredential(pageIdentifier)?.token ?? null;
}

export function getRefreshTokenForOrganization(
  organizationIdentifier: string,
): string | null {
  return (
    credentialEntry(organizationStorageKey(organizationIdentifier)).get()
      ?.token ?? null
  );
}

export function getRefreshTokensForRegion(identifier: string): string[] {
  const keyPrefix = organizationStorageKey(`${parseRegion(identifier)}_`);

  try {
    // Reading prunes expired entries, which reindexes localStorage, so collect
    // the keys before reading any of them.
    const keys = Array.from({length: localStorage.length}, (_, index) =>
      localStorage.key(index),
    ).filter((key): key is string => key?.startsWith(keyPrefix) ?? false);

    const tokens = keys.flatMap((key) => {
      const stored = credentialEntry(key).get();
      return stored ? [stored.token] : [];
    });

    return [...new Set(tokens)];
  } catch {
    return [];
  }
}

// A viewer session belongs to the identity that minted it, so replacing the
// organization's credential with a different identity invalidates it. That
// includes a previous credential that has already expired out of storage: the
// session it minted may still be live.
function replaceOrganizationCredential(
  organizationIdentifier: string,
  credential: StoredRefreshToken,
): void {
  const entry = credentialEntry(organizationStorageKey(organizationIdentifier));
  const previous = entry.get();

  entry.set(credential);

  if (previous?.token !== credential.token) {
    clearViewerSessionForOrganization(organizationIdentifier);
  }
}

export function storeRefreshToken(
  pageIdentifier: string,
  organizationIdentifier: string,
  token: string,
  expiresAt: string,
  {anonymous}: {anonymous: boolean},
): void {
  replaceOrganizationCredential(organizationIdentifier, {
    token,
    expiresAt,
    anonymous,
  });
  writeOrganizationPointers(pageIdentifier, organizationIdentifier);
  credentialEntry(legacyStorageKey(pageIdentifier)).clear();
}

export function associateRefreshToken(
  pageIdentifier: string,
  organizationIdentifier: string,
  expectedToken: string,
): void {
  const stored = credentialEntry(
    organizationStorageKey(organizationIdentifier),
  ).get();
  const credential = getRefreshCredential(pageIdentifier);

  if (stored?.token !== expectedToken) {
    // Another request may have replaced the credential while this one was in
    // flight; only the credential this page still resolves to may migrate.
    if (credential?.token !== expectedToken) {
      return;
    }
    // An organization holds one identity, and a verified one is already in
    // place. A legacy page-scoped entry for a different identity is not
    // promoted over it; it keeps serving only its own page until it expires.
    if (stored && !stored.anonymous) {
      return;
    }
    replaceOrganizationCredential(organizationIdentifier, {
      token: credential.token,
      expiresAt: credential.expiresAt,
      anonymous: false,
    });
  }

  writeOrganizationPointers(pageIdentifier, organizationIdentifier);

  if (credential?.storageKey === legacyStorageKey(pageIdentifier)) {
    credentialEntry(credential.storageKey).clear();
  }
}
